/**
 * SessionCard - the dashboard's cliffhanger resume card
 *
 * Two resumable states from `/api/session/resume-context`:
 * - LIVE: the party is mid-session; the CTA navigates into `/play/:id`.
 * - ENDED_INCOMPLETE: the last run ended with the module unfinished; the
 *   leader's CTA drops a one-shot auto-start flag and heads to the lobby
 *   (LobbyLayout fires `startGame` when the flag is fresh); non-leaders get
 *   "Return to Camp".
 *
 * View derivation is pure, see lib/resume-card.ts.
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GameTimeDisplay } from '@/components/game/GameTimeDisplay';
import { buildResumeCard, resolveSceneMediaUrl, RESUME_AUTO_START_KEY } from '@/lib/resume-card';
import type { ResumeContext } from '@/lib/api';
import { getMediaBucketUrl, getModuleHeroUrl } from '@/lib/media';
import { track } from '@/lib/analytics';
import { useEffect, useRef } from 'react';

/** Resolve a raw `data/modules/...` path to its CDN URL (same mapping the
 *  backend uses for module media). */
function resolveSceneMediaPath(path: string): string {
  // Pure resolution logic lives in resume-card.ts so it can be unit-tested
  // without pulling in this component; here we just bind the media bucket.
  return resolveSceneMediaUrl(path, getMediaBucketUrl());
}

interface SessionCardProps {
  resumeContext: ResumeContext | null;
  /** The viewer's character leads the party (ENDED CTA gating). */
  isLeader: boolean;
  finalGameTimeSeconds?: number;
  disabled?: boolean;
  /** Override live-resume navigation. Used to gate mobile viewports to
   *  /get-the-app instead of the desktop-only game UI. */
  onResume?: (sessionId: string) => void;
  /** Override lobby navigation for the ENDED CTAs (Continue / Return to
   *  Camp), same mobile /get-the-app gate as every other lobby entry. */
  onEnterLobby?: () => void;
}

export default function SessionCard({
  resumeContext,
  isLeader,
  finalGameTimeSeconds,
  disabled,
  onResume,
  onEnterLobby,
}: SessionCardProps) {
  const navigate = useNavigate();

  const view = resumeContext
    ? buildResumeCard({
        context: resumeContext,
        isLeader,
        resolveMediaPath: resolveSceneMediaPath,
        moduleHeroUrl: getModuleHeroUrl,
      })
    : null;

  // resume_card_shown once per mount per state (not per re-render).
  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (view && shownRef.current !== resumeContext?.state) {
      shownRef.current = resumeContext?.state ?? null;
      track('resume_card_shown', {
        state: resumeContext?.state,
        encounter_type: resumeContext?.encounterType,
      });
    }
  }, [view, resumeContext?.state, resumeContext?.encounterType]);

  if (!view) return null;

  const handleCta = () => {
    track('resume_card_click', {
      state: resumeContext?.state,
      encounter_type: resumeContext?.encounterType,
    });
    if (view.ctaKind === 'resume-live' && view.activeSessionId) {
      if (onResume) {
        onResume(view.activeSessionId);
      } else {
        navigate(`/play/${view.activeSessionId}`);
      }
      return;
    }
    if (view.ctaKind === 'continue-adventure') {
      // One-shot handoff: LobbyLayout fires startGame(currentPartyId) when
      // the flag is fresh (60s TTL); failures surface via Tavian party chat.
      // Harmless if the mobile gate below diverts to /get-the-app: the flag
      // expires unread.
      sessionStorage.setItem(RESUME_AUTO_START_KEY, String(Date.now()));
    }
    if (onEnterLobby) {
      onEnterLobby();
    } else {
      navigate('/lobby');
    }
  };

  return (
    <div
      className="rounded-xl border bg-card/40 text-card-foreground shadow-lg backdrop-blur-md overflow-hidden"
      data-testid="resume-card"
    >
      {view.artUrl && (
        <img
          src={view.artUrl}
          alt={view.subtitle ?? 'Adventure scene'}
          className="w-full aspect-[21/9] object-cover"
          onError={(e) => {
            // Broken art degrades to a text-only card.
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      <div className="p-6 text-center space-y-3">
        <div>
          <h3 className="text-lg font-bold text-foreground">{view.title}</h3>
          {view.subtitle && <p className="text-sm text-muted-foreground">{view.subtitle}</p>}
        </div>
        {view.recap && (
          <p className="text-sm text-muted-foreground italic leading-relaxed">{view.recap}</p>
        )}
        {finalGameTimeSeconds != null && view.ctaKind !== 'resume-live' && (
          <div className="flex justify-center">
            <GameTimeDisplay seconds={finalGameTimeSeconds} />
          </div>
        )}
        <Button
          className="bg-green-600 hover:bg-green-700 text-white"
          disabled={disabled}
          onClick={handleCta}
        >
          {view.ctaLabel}
        </Button>
      </div>
    </div>
  );
}
